import {
  Injectable,
  OnDestroy,
  inject,
  DestroyRef,
  signal,
  WritableSignal,
} from "@angular/core";
import { HttpClient } from '@angular/common/http';
import { DOCUMENT } from "@angular/common";
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { LoggerService } from './logger.service';
import { AuthService } from './auth.service';
import { Subject, Observable, timer, EMPTY, fromEvent } from "rxjs";
import {
  retry,
  distinctUntilChanged,
} from "rxjs/operators";
import { ConfigStore } from '../../stores/config.store';
import { WebSocketEnvelopeWireSchema } from './websocket-schemas';
import {
  SubscriptionIntent,
  updateSubscriptionIntents,
} from "./websocket-subscription-intents";

export type WebSocketMessageType =
  | 'connection' | 'message' | 'error' | 'trade' | 'alert' | 'market_update' | 'system_health' | 'order_update' | 'position_update'
  | 'market_data' | 'market_data_update' | 'market_data_unavailable'
  // Permission-related message types
  | 'user_created' | 'role_created' | 'role_updated' | 'role_deleted'
  | 'permission_granted' | 'permission_revoked' | 'user_role_assigned' | 'user_role_removed'
  | 'permissions_updated' | 'cache_invalidated'
  // HFT metrics message types (from /ws/metrics/live)
  | 'auth_result' | 'subscribed' | 'unsubscribed' | 'settings_change'
  | 'snapshot' | 'latency' | 'throughput' | 'percentiles' | 'system' | 'event_loop'
  | 'exchange_connectivity_update' | 'fill_rate_analytics_update' | 'pong'
  // Typed performance producer snapshots
  | 'web_vitals_update' | 'pages_update' | 'api_update' | 'errors_update' | 'system_update'
  // Client portal message types
  | 'portfolio_update' | 'new_alert'
  // Regulatory reporting message types
  | 'report_status_update' | 'compliance_violation'
  // Heat map message types
  | 'heat_map_update' | 'heat_map_event'
  // API management message types
  | 'metrics_update' | 'api_event'
  // Multi-tenant dashboard message types (from /ws/multi-tenant)
  | 'usage_update' | 'tenant_alert'
  // ML/Analytics message types
  | 'anomaly_detected' | 'anomaly_updated' | 'pnl_update'
  // Predictive maintenance message types
  | 'health_update' | 'failure_prediction'
  // Generic fallback
  | 'invalid';

/**
 * Generic WebSocket envelope. The `data` field is typed via the generic
 * `TData` parameter, defaulting to `unknown` so call sites that don't
 * specify a payload type get a safe, narrow-able value (was `any`).
 *
 * Consumers that know their payload shape parameterise the generic at
 * the boundary, e.g. `WebSocketMessage<TradeUpdate>`, or perform a
 * structural cast on `message.data` inside switch-branches keyed on
 * `message.type`. The `Zod` schema in `websocket-schemas.ts` validates
 * the envelope shape at the producer; downstream narrowing aligns with
 * what Zod has already accepted.
 */
export interface WebSocketMessage<TData = unknown> {
  type: WebSocketMessageType;
  data: TData;
  timestamp: Date;
  source?: string;
}

export interface WebSocketConfig {
  url: string;
  reconnect?: boolean;
  reconnectInterval?: number;
  maxReconnectAttempts?: number;
  heartbeatInterval?: number;
  /** Deadline for a peer response to an application heartbeat (default: 30000ms). */
  heartbeatTimeout?: number;
  /** Enable fallback to HTTP polling when WebSocket fails */
  enablePollingFallback?: boolean;
  /** Polling interval in ms (default: 5000) */
  pollingInterval?: number;
}

/** Recoverable endpoint lifecycle, including the outage signal that does not error its data stream. */
export interface WebSocketLifecycle {
  status: "connecting" | "connected" | "disconnected" | "error";
  reconnectAttempt: number;
  maxReconnectAttempts: number;
  retriesExhausted: boolean;
  healthy: boolean;
}

@Injectable({
  providedIn: "root",
})
export class WebSocketService implements OnDestroy {
  private configStore = inject(ConfigStore);
  private logger = inject(LoggerService);
  private authService = inject(AuthService);
  private destroyRef = inject(DestroyRef);
  private readonly document = inject(DOCUMENT);

  // Public HttpClient for use by other services
  readonly httpClient = inject(HttpClient);

  // Connection status signal — aggregated from all endpoint states.
  // Priority: error > connecting > connected > disconnected
  // Protected: subclasses managing a single endpoint set this directly;
  // the base class uses updateAggregateStatus() for multi-endpoint aggregation.
  protected readonly connectionStatusCache: WritableSignal<'connecting' | 'connected' | 'disconnected' | 'error'> = signal('disconnected');
  readonly connectionStatus = this.connectionStatusCache.asReadonly();
  readonly connectionStatus$ = toObservable(this.connectionStatus);
  private websockets = new Map<string, WebSocket>();
  private messageSubjects = new Map<string, Subject<WebSocketMessage>>();
  private connectionStates = new Map<string, 'connecting' | 'connected' | 'disconnected' | 'error'>();
  private reconnectAttempts = new Map<string, number>();
  private heartbeatIntervals = new Map<string, ReturnType<typeof setInterval>>();
  private readonly heartbeatDeadlines = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();
  private readonly reconnectTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();
  private readonly exhaustedEndpoints = new Set<string>();
  private readonly healthyEndpoints = new Set<string>();
  private readonly desiredMarketSubscriptions = new Map<string, SubscriptionIntent>();
  private hasExplicitMarketIntent = false;
  private readonly defaultMarketTopics = new Set<string>();
  // MEMORY-LEAK FIX: reference count of live owners per endpoint. Several
  // independent services share a single endpoint socket (e.g. the dashboard
  // store AND the dashboard aggregator both own 'trading'; the root PnLService
  // AND the aggregator's PnLWebSocketService both own 'pnl'). Each connect()
  // acquires (count++); each disconnect() releases (count--) and the socket is
  // only torn down when the LAST owner releases (count hits 0). Without this,
  // one owner leaving a route would either (a) close a socket another owner
  // still depends on — permanently killing its stream, since some owners only
  // connect() once at construction — or (b) leave orphaned sockets when opens
  // and closes interleaved across owners. Forced teardown (logout / app
  // destroy) bypasses the count.
  private subscriberCounts = new Map<string, number>();
  // Track which endpoints need re-authentication after token refresh
  private pendingAuthEndpoints = new Set<string>();
  // Track authentication state per endpoint
  private authenticatedEndpoints = new Set<string>();
  // Queue subscriptions for when connection is established
  private pendingSubscriptions = new Map<string, SubscriptionIntent[]>();
  // Track endpoint configurations for reconnection
  private endpointConfigs = new Map<string, WebSocketConfig>();
  // Endpoints whose connect() was requested while UNAUTHENTICATED. We register
  // a live message Subject (so subscribers keep a valid observable) but do NOT
  // open a socket. These are drained — actually opened — on the next
  // authenticated transition. This prevents feature sockets (system-health,
  // trading, pnl, settings, ...) from flooding the console with auth-failed
  // handshakes on /auth/* routes before the user has logged in.
  private deferredEndpoints = new Map<string, WebSocketConfig>();
  // Fallback polling timers
  private pollingTimers = new Map<string, ReturnType<typeof setInterval>>();
  private readonly defaultConfig: Partial<WebSocketConfig> = {
    reconnect: true,
    reconnectInterval: 5000,
    maxReconnectAttempts: 10,
    heartbeatInterval: 30000,
    heartbeatTimeout: 30000,
    enablePollingFallback: false,
    pollingInterval: 5000,
  };

  /**
   * Per-endpoint state-change notifier for getConnectionStatus$(). The
   * aggregate signal alone is a LOSSY notifier: it can stay unchanged (e.g.
   * pinned 'error' by one endpoint) while another endpoint transitions, so
   * per-endpoint observers would miss real disconnect/reconnect cycles.
   */
  private readonly endpointStatusChanged$ = new Subject<string>();

  /**
   * Set (or clear) a per-endpoint connection state and notify BOTH the
   * aggregate signal and per-endpoint observers. All connectionStates
   * mutations MUST go through here.
   */
  private setEndpointState(
    endpoint: string,
    state: "connecting" | "connected" | "disconnected" | "error" | null,
  ): void {
    if (state === null) {
      this.connectionStates.delete(endpoint);
    } else {
      this.connectionStates.set(endpoint, state);
    }
    this.updateAggregateStatus();
    this.endpointStatusChanged$.next(endpoint);
  }

  /**
   * Recomputes the aggregate connection status from all per-endpoint states.
   * Priority: error > connecting > connected > disconnected.
   */
  private updateAggregateStatus(): void {
    const states = Array.from(this.connectionStates.values());
    if (states.length === 0) {
      this.connectionStatusCache.set('disconnected');
      return;
    }
    if (states.includes('error')) {
      this.connectionStatusCache.set('error');
    } else if (states.includes('connecting')) {
      this.connectionStatusCache.set('connecting');
    } else if (states.includes('connected')) {
      this.connectionStatusCache.set('connected');
    } else {
      this.connectionStatusCache.set('disconnected');
    }
  }

  constructor() {
    fromEvent(this.document, "visibilitychange")
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        for (const endpoint of this.heartbeatIntervals.keys()) {
          this.clearHeartbeatDeadline(endpoint);
          // A resumed tab gets a fresh probe and a full acknowledgement window;
          // a suspended timer is not evidence of a failed transport.
          if (this.document.visibilityState !== "hidden") {
            this.sendHeartbeat(endpoint);
          }
        }
      });
    // Listen for authentication state changes to re-authenticate WebSocket connections
    this.authService.isAuthenticated$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (isAuthenticated) => {
          if (isAuthenticated) {
            // Re-authenticate all pending endpoints when user logs in or token refreshes
            this.reauthenticateAllConnections();
            // Open any endpoints whose connect() was deferred while unauthenticated.
            this.drainDeferredEndpoints();
          } else {
            // User logged out: tear down EVERY live endpoint so the exponential
            // backoff reconnect loop (ws.onclose) does not keep re-handshaking
            // against now-unauthenticated routes. Clearing only the
            // authenticated-endpoint set (the previous behaviour) left sockets
            // and their reconnect timers running, flooding the console after the
            // redirect to /auth/login. disconnect() closes the socket AND cancels
            // its reconnect/heartbeat timers.
            const liveEndpoints = new Set<string>([
              ...this.websockets.keys(),
              ...this.messageSubjects.keys(),
            ]);
            // force: a logout must close every socket regardless of outstanding
            // owner references — no route/component will run its disconnect() once
            // we redirect to /auth/login, so honouring the count here would strand
            // authenticated sockets reconnecting against a logged-out session.
            liveEndpoints.forEach((endpoint) =>
              this.disconnect(endpoint, true),
            );
            this.deferredEndpoints.clear();
            this.authenticatedEndpoints.clear();
          }
        },
        error: (error: unknown) => {
          // Auth subscription error - log and handle based on error type
          this.logger.error("Auth subscription error:", { error });

          // Only clear authenticated endpoints for auth-related errors (401/403)
          // For transient errors, keep state to allow reconnection
          const isAuthError =
            error instanceof Error &&
            (error.message.includes("401") ||
              error.message.includes("403") ||
              error.message.includes("Unauthorized") ||
              error.message.includes("Forbidden") ||
              error.message.includes("token"));

          if (isAuthError) {
            this.authenticatedEndpoints.clear();
            this.logger.warn(
              "Cleared authenticated endpoints due to auth error",
            );
          }
        },
      });
  }

  /**
   * Connect to a WebSocket endpoint
   * Manages connection lifecycle, reconnection, and subscription queuing
   */
  connect(
    endpoint: string,
    config?: Partial<WebSocketConfig>,
  ): Observable<WebSocketMessage> {
    const fullConfig = { ...this.defaultConfig, ...config } as WebSocketConfig;
    const url = config?.url || this.buildWebSocketUrl(endpoint);

    // Store config for reconnection logic
    this.endpointConfigs.set(endpoint, fullConfig);

    // MEMORY-LEAK FIX: acquire one owner reference for this endpoint. Released
    // by the matching disconnect(endpoint). The socket survives until the count
    // returns to zero — see subscriberCounts declaration.
    this.subscriberCounts.set(
      endpoint,
      (this.subscriberCounts.get(endpoint) ?? 0) + 1,
    );

    // AUTH GATE (chokepoint): feature WebSocket endpoints MUST NOT open until
    // the user is authenticated. Every feature service AND
    // WebSocketResilienceService route through connect(), so gating here covers
    // them all without touching the callers. We still register/return a live
    // message Subject so subscribers get a valid observable; the socket is
    // opened later by drainDeferredEndpoints() on the next auth=true transition.
    if (!this.authService.isAuthenticated()) {
      let messageSubject = this.messageSubjects.get(endpoint);
      if (!messageSubject) {
        messageSubject = new Subject<WebSocketMessage>();
        this.messageSubjects.set(endpoint, messageSubject);
        this.reconnectAttempts.set(endpoint, 0);
      }
      this.deferredEndpoints.set(endpoint, fullConfig);
      this.logger.debug(`WebSocket ${endpoint} deferred until authenticated`);
      return this.wrapEndpointObservable(endpoint, messageSubject, fullConfig);
    }

    if (this.messageSubjects.has(endpoint)) {
      // Return existing observable but ensure connection is active
      const existingState = this.connectionStates.get(endpoint);
      if (existingState === "disconnected" || existingState === "error") {
        // Re-initiate connection
        this.createWebSocket(endpoint, url, fullConfig);
      }
      return this.messageSubjects.get(endpoint)!.asObservable();
    }

    const messageSubject = new Subject<WebSocketMessage>();
    this.messageSubjects.set(endpoint, messageSubject);
    this.reconnectAttempts.set(endpoint, 0);

    this.createWebSocket(endpoint, url, fullConfig);

    return this.wrapEndpointObservable(endpoint, messageSubject, fullConfig);
  }

  /**
   * Wrap an endpoint's message Subject with the shared retry/reconnect policy
   * and lifecycle teardown. Extracted so both the live and the
   * deferred-until-authenticated paths return an identically-behaving stream.
   */
  private wrapEndpointObservable(
    endpoint: string,
    messageSubject: Subject<WebSocketMessage>,
    fullConfig: WebSocketConfig,
  ): Observable<WebSocketMessage> {
    return messageSubject.asObservable().pipe(
      takeUntilDestroyed(this.destroyRef),
      retry({
        delay: (error, retryCount) => {
          // ADMIN-INTERFACE-73: fires on every retry — expected/recoverable, so log
          // at debug (below prod/staging thresholds → never pages Sentry). The
          // give-up below (return EMPTY) is the terminal signal.
          this.logger.debug(`WebSocket error on ${endpoint}:`, error);
          if (
            !fullConfig.reconnect ||
            retryCount >= (fullConfig.maxReconnectAttempts || 10)
          ) {
            return EMPTY;
          }
          this.reconnectAttempts.set(endpoint, retryCount);
          return timer(fullConfig.reconnectInterval || 5000);
        },
      }),
    );
  }

  /**
   * Send a message through a WebSocket connection
   * Wraps message with required fields for backend WsMessage struct
   * Queues subscription messages when not connected for replay on reconnect
   */
  // GAP-FE-A01 r127 (Opus-Max v96): `message: any` → structural shape.
  // The function reads `message.type`, `message.topic`, `message.payload`
  // and falls back to the whole `message` as payload when undefined.
  // The minimal-shape narrowing forces callers to satisfy at least the
  // outgoing-message contract without losing the ability to pass
  // arbitrary payloads through.
  send(
    endpoint: string,
    message:
      | { type?: string; topic?: string; payload?: unknown; channels?: unknown }
      | unknown,
  ): boolean {
    const msg = message as {
      type?: string; topic?: string; symbols?: unknown; channels?: unknown; events?: unknown; payload?: unknown;
    } | null;
    if (endpoint !== 'market-data' || !msg || !['subscribe', 'unsubscribe'].includes(msg.type ?? '')) {
      return this.writeMessage(endpoint, message);
    }
    const payload = msg.payload !== null && typeof msg.payload === 'object'
      ? msg.payload as Record<string, unknown> : undefined;
    const symbols = msg.symbols ?? payload?.['symbols'];
    const channels = msg.channels ?? payload?.['channels'];
    const events = msg.events ?? payload?.['events'];
    if (events !== undefined || (channels !== undefined && (!Array.isArray(channels) ||
      !channels.every(channel => ['prices', 'trades', 'orderbook', 'ticker'].includes(channel))))) {
      return this.writeMessage(endpoint, message);
    }
    const frames: unknown[] = [];
    if (msg.topic?.startsWith('market:')) {
      frames.push(message);
    } else if (Array.isArray(symbols) && symbols.every(symbol => typeof symbol === 'string' && symbol.length > 0)) {
      for (const symbol of new Set(symbols)) {
        frames.push({ ...msg, topic: `market:${symbol}`, symbols: [symbol],
          payload: { ...(payload ?? msg), symbols: [symbol] } });
      }
      if (symbols.length === 0 && msg.type === 'unsubscribe') {
        frames.push(...Array.from(this.desiredMarketSubscriptions.values(), intent => ({ ...intent, type: 'unsubscribe' })));
      }
    } else {
      // Preserve unsupported selectors without relabeling orders/positions as prices.
      return this.writeMessage(endpoint, message);
    }
    this.hasExplicitMarketIntent = true;
    for (const frame of frames) {
      const intent = frame as SubscriptionIntent;
      if (intent.topic) {
        if (intent.type === 'subscribe') { this.desiredMarketSubscriptions.set(intent.topic, intent); }
        else { this.desiredMarketSubscriptions.delete(intent.topic); }
      }
    }
    // Defaults are only the pre-selection state. Once explicit intent arrives,
    // remove them even when that intent is an empty set.
    for (const topic of this.defaultMarketTopics) {
      this.writeMessage(endpoint, { type: 'unsubscribe', topic, payload: { topic } });
    }
    this.defaultMarketTopics.clear();
    let sent = true;
    for (const frame of frames) { sent = this.writeMessage(endpoint, frame) && sent; }
    return frames.length > 0 && sent;
  }

  private writeMessage(endpoint: string, message: unknown): boolean {
    const ws = this.websockets.get(endpoint);
    // Treat all incoming messages as the structural shape internally.
    const msg = message as {
      type?: string;
      topic?: string;
      payload?: unknown;
      channels?: unknown;
    };
    if (ws && ws.readyState === WebSocket.OPEN) {
      // Settings uses its typed, internally tagged Rust protocol rather than
      // the shared WsMessage envelope. Keep its user/session fields top-level.
      if (endpoint === 'settings') {
        ws.send(JSON.stringify(message));
        return true;
      }
      // Backend requires: id (UUID), type, topic, payload, timestamp
      const fullMessage: Record<string, unknown> = {
        id: crypto.randomUUID(),
        type: msg.type || "message",
        topic: msg.topic || endpoint,
        payload: msg.payload !== undefined ? msg.payload : message,
        timestamp: new Date().toISOString(),
      };
      // WS-PERFORMANCE-CONTRACT: forward a TOP-LEVEL `channels` array when the
      // caller supplies one. The performance WS handler parses subscribe/unsubscribe
      // as `{ type, channels: [...] }` and reads `channels` at the top level — the
      // default `payload` nesting buries it, so channel selection was silently
      // ignored (the backend fell back to "all"). Other endpoints never set this
      // field, so their frames are unchanged.
      if (Array.isArray(msg.channels)) {
        fullMessage["channels"] = msg.channels;
      }
      ws.send(JSON.stringify(fullMessage));
      return true;
    } else {
      // Queue subscription messages for replay when connection is established
      if (endpoint === "market-data" && this.hasExplicitMarketIntent) { return false; }
      if (msg.type === "subscribe" || msg.type === "unsubscribe") {
        const pending = this.pendingSubscriptions.get(endpoint) || [];
        this.pendingSubscriptions.set(
          endpoint,
          updateSubscriptionIntents(pending, message),
        );
        this.logger.debug(
          `Queued ${msg.type} for ${endpoint} (WebSocket not connected)`,
        );
      } else {
        this.logger.warn(
          `WebSocket ${endpoint} is not connected, message dropped`,
        );
      }
      return false;
    }
  }

  /**
   * Disconnect from a WebSocket endpoint
   * Cleans up all associated resources including pending subscriptions and polling timers
   */
  disconnect(endpoint: string, force = false): void {
    // MEMORY-LEAK FIX: release one owner reference. The socket is torn down
    // ONLY when the last owner releases (count reaches 0), or when `force` is
    // set (logout / app destroy — see callers). A disconnect() with no
    // outstanding count still runs the idempotent teardown below (safe: every
    // delete/clear is guarded), so a defensive disconnect() never throws.
    if (!force) {
      const remaining = (this.subscriberCounts.get(endpoint) ?? 0) - 1;
      if (remaining > 0) {
        this.subscriberCounts.set(endpoint, remaining);
        return; // other owners still need this socket — keep it open
      }
    }
    this.subscriberCounts.delete(endpoint);
    if (endpoint === 'market-data') {
      this.desiredMarketSubscriptions.clear();
      this.hasExplicitMarketIntent = false;
      this.defaultMarketTopics.clear();
    }
    this.clearConnectionTimers(endpoint);
    this.exhaustedEndpoints.delete(endpoint);
    this.healthyEndpoints.delete(endpoint);

    const ws = this.websockets.get(endpoint);
    if (ws) {
      // Remove handlers first to prevent triggering reconnect
      ws.onclose = null;
      ws.onerror = null;
      ws.onmessage = null;
      ws.onopen = null;
      ws.close();
      this.websockets.delete(endpoint);
    }

    const subject = this.messageSubjects.get(endpoint);
    if (subject) {
      subject.complete();
      this.messageSubjects.delete(endpoint);
    }

    const interval = this.heartbeatIntervals.get(endpoint);
    if (interval) {
      clearInterval(interval);
      this.heartbeatIntervals.delete(endpoint);
    }

    // Clean up polling timer
    const pollingTimer = this.pollingTimers.get(endpoint);
    if (pollingTimer) {
      clearInterval(pollingTimer);
      this.pollingTimers.delete(endpoint);
    }

    this.setEndpointState(endpoint, null);
    this.reconnectAttempts.delete(endpoint);
    this.endpointConfigs.delete(endpoint);
    this.pendingSubscriptions.delete(endpoint);
    // Clean up auth tracking
    this.pendingAuthEndpoints.delete(endpoint);
    this.authenticatedEndpoints.delete(endpoint);
  }

  /**
   * Get the connection state of a WebSocket endpoint
   */
  getConnectionState(
    endpoint: string,
  ): "connecting" | "connected" | "disconnected" | "error" | null {
    return this.connectionStates.get(endpoint) || null;
  }

  /**
   * Check if an endpoint is connected
   */
  isConnected(endpoint: string): boolean {
    return this.connectionStates.get(endpoint) === 'connected';
  }

  /**
   * Check if WebSocket is in a state where subscriptions can be queued
   * Returns true if connected OR if subscriptions will be queued for later
   */
  canSubscribe(endpoint: string): boolean {
    const state = this.connectionStates.get(endpoint);
    // Can subscribe if connected, connecting, or if endpoint is registered
    return (
      state === "connected" ||
      state === "connecting" ||
      this.messageSubjects.has(endpoint)
    );
  }

  /**
   * Get observable of connection status changes for a specific endpoint
   */
  getConnectionStatus$(
    endpoint: string,
  ): Observable<"connecting" | "connected" | "disconnected" | "error"> {
    // Return a filtered version of the global status that only emits for this endpoint
    return new Observable((subscriber) => {
      // Deduplicate per subscriber: both notifier sources below can deliver
      // the same state repeatedly (e.g. audit.service re-subscribes filters
      // on every 'connected' emission — a duplicate would resend them).
      let lastEmitted:
        "connecting" | "connected" | "disconnected" | "error" | null = null;
      const emit = () => {
        const state = this.connectionStates.get(endpoint) || "disconnected";
        if (state !== lastEmitted) {
          lastEmitted = state;
          subscriber.next(state);
        }
      };

      // Emit current state immediately
      emit();

      // Per-endpoint notifier: fires on EVERY state change of this endpoint,
      // including ones the aggregate signal cannot observe (see
      // endpointStatusChanged$).
      const perEndpointSub = this.endpointStatusChanged$.subscribe(
        (changed) => {
          if (changed === endpoint) {
            emit();
          }
        },
      );

      // Aggregate changes kept as a compatibility backstop.
      const sub = this.connectionStatus$.subscribe(() => emit());

      return () => {
        perEndpointSub.unsubscribe();
        sub.unsubscribe();
      };
    });
  }

  /**
   * Get the number of pending subscriptions for an endpoint
   */
  getPendingSubscriptionCount(endpoint: string): number {
    if (endpoint === 'market-data' && this.hasExplicitMarketIntent && !this.isConnected(endpoint)) {
      return this.desiredMarketSubscriptions.size;
    }
    return this.pendingSubscriptions.get(endpoint)?.length || 0;
  }

  /**
   * Get the current reconnect attempt count for an endpoint.
   *
   * Returns the live value tracked in `ws.onclose` (incremented on each
   * reconnect, reset to 0 after a valid peer response). Returns 0 for endpoints
   * that have never connected. Consumers use this to render an accurate
   * reconnect counter rather than a hardcoded placeholder.
   */
  getReconnectAttempt(endpoint: string): number {
    return this.reconnectAttempts.get(endpoint) || 0;
  }

  getConnectionLifecycle$(endpoint: string): Observable<WebSocketLifecycle> {
    return new Observable<WebSocketLifecycle>((subscriber) => {
      const emit = () =>
        subscriber.next({
          status: this.getConnectionState(endpoint) ?? "disconnected",
          reconnectAttempt: this.getReconnectAttempt(endpoint),
          maxReconnectAttempts: this.getMaxReconnectAttempts(endpoint),
          retriesExhausted: this.exhaustedEndpoints.has(endpoint),
          healthy: this.healthyEndpoints.has(endpoint),
        });
      emit();
      const changed = this.endpointStatusChanged$.subscribe((value) => {
        if (value === endpoint) {
          emit();
        }
      });
      const aggregate = this.connectionStatus$.subscribe(emit);
      return () => {
        changed.unsubscribe();
        aggregate.unsubscribe();
      };
    }).pipe(
      distinctUntilChanged(
        (previous, current) =>
          previous.status === current.status &&
          previous.reconnectAttempt === current.reconnectAttempt &&
          previous.maxReconnectAttempts === current.maxReconnectAttempts &&
          previous.retriesExhausted === current.retriesExhausted &&
          previous.healthy === current.healthy,
      ),
    );
  }

  /**
   * Get the configured maximum reconnect attempts for an endpoint.
   *
   * Sourced from the endpoint's stored config (falling back to the service
   * default of 10 when the endpoint has not yet been configured).
   */
  getMaxReconnectAttempts(endpoint: string): number {
    return (
      this.endpointConfigs.get(endpoint)?.maxReconnectAttempts ??
      this.defaultConfig.maxReconnectAttempts ??
      10
    );
  }

  /**
   * Manually trigger reconnection for an endpoint
   */
  reconnect(endpoint: string): void {
    const config = this.endpointConfigs.get(endpoint);
    if (!config) {
      this.logger.warn(
        `Cannot reconnect to ${endpoint}: no configuration found`,
      );
      return;
    }

    this.logger.info(`Manual reconnection requested for ${endpoint}`);
    this.reconnectAttempts.set(endpoint, 0);
    this.exhaustedEndpoints.delete(endpoint);
    const url = config.url || this.buildWebSocketUrl(endpoint);
    this.createWebSocket(endpoint, url, config);
  }

  ngOnDestroy(): void {
    // Close all WebSocket connections. force: the service (and the whole app)
    // is being destroyed — tear every socket down regardless of owner count.
    this.messageSubjects.forEach((subject, endpoint) => {
      this.disconnect(endpoint, true);
    });
  }

  private createWebSocket(
    endpoint: string,
    url: string,
    config: Partial<WebSocketConfig>,
  ): void {
    this.clearConnectionTimers(endpoint);
    this.healthyEndpoints.delete(endpoint);
    if (endpoint === "market-data") { this.defaultMarketTopics.clear(); }
    try {
      // CRITICAL FIX: Close any existing WebSocket before creating a new one
      // This prevents memory leaks from orphaned WebSocket connections
      const existingWs = this.websockets.get(endpoint);
      if (existingWs) {
        try {
          // Remove handlers to prevent triggering another reconnect
          existingWs.onclose = null;
          existingWs.onerror = null;
          existingWs.onmessage = null;
          existingWs.onopen = null;
          if (
            existingWs.readyState === WebSocket.OPEN ||
            existingWs.readyState === WebSocket.CONNECTING
          ) {
            existingWs.close();
          }
        } catch {
          // Ignore errors when closing old WebSocket
        }
      }

      // SECURITY: deliver the JWT at upgrade time via the Sec-WebSocket-Protocol
      // header (the browser sends `protocols` there) instead of the URL. The
      // dedicated WS handlers (/ws/system-health, /ws/risk, /ws/admin, alerts,
      // order-diagnostics) require an upgrade-time JWT and close the socket
      // otherwise — the post-connection `authenticate` message (sent below in
      // onopen) lands too late, causing the connect/disconnect reconnect loop.
      // The backend extracts + validates the JWT-shaped subprotocol and echoes
      // it in the 101. The token is read here (not captured earlier) so every
      // reconnect through createWebSocket picks up the current/refreshed token.
      // No token (genuinely public endpoints) → anonymous connect, unchanged.
      const token = this.authService.getToken();
      const ws = token ? new WebSocket(url, [token]) : new WebSocket(url);
      this.websockets.set(endpoint, ws);
      this.setEndpointState(endpoint, "connecting");

      ws.onopen = () => {
        if (this.websockets.get(endpoint) !== ws) {
          return;
        }
        this.logger.info(`WebSocket connected to ${endpoint}`);

        // SECURITY: Send authentication message first (token NOT in URL)
        this.sendAuthenticationMessage(endpoint);

        // Start heartbeat if configured
        if (config.heartbeatInterval) {
          this.startHeartbeat(endpoint, config.heartbeatInterval);
        }

        // Send initial subscription message if needed
        this.sendInitialSubscription(endpoint);
        // Upgrade credentials and the compatibility auth envelope precede
        // feature subscription replay triggered by this transition.
        this.setEndpointState(endpoint, "connected");
      };

      ws.onmessage = (event) => {
        if (this.websockets.get(endpoint) !== ws) {
          return;
        }
        try {
          // Stage 1 — JSON.parse can still throw on malformed payloads; the
          // outer try/catch below catches that.
          const raw: unknown = JSON.parse(event.data);

          // Stage 2 — Zod envelope validation (GAP-FE-003). Drop wire-level
          // garbage (non-object payloads, wrong-typed top-level fields)
          // BEFORE distributing to per-endpoint Subjects. Per-payload
          // validation is the responsibility of the consumer service.
          const parsed = WebSocketEnvelopeWireSchema.safeParse(raw);
          if (!parsed.success) {
            this.logger.warn(
              `[WebSocket] dropped malformed envelope from ${endpoint}`,
              {
                issues: parsed.error.flatten(),
                endpoint,
              },
            );
            return;
          }

          const wire = parsed.data;
          if (typeof wire.type === "string" && wire.type) { this.clearHeartbeatDeadline(endpoint); }
          const authPayload = (wire.data ?? wire.payload ?? wire) as { success?: unknown };
          const rejectedAuth = wire.type === "auth_result" && authPayload.success === false;
          if (typeof wire.type === 'string' && wire.type.length > 0 && wire.type !== 'error' && !rejectedAuth && !this.healthyEndpoints.has(endpoint)) {
            this.healthyEndpoints.add(endpoint);
            this.exhaustedEndpoints.delete(endpoint);
            this.reconnectAttempts.set(endpoint, 0);
            this.endpointStatusChanged$.next(endpoint);
          }
          // Canonical WsMessage envelopes carry `payload`; older producers
          // use `data`, and a few internally tagged messages keep fields at
          // the top level.
          const data: unknown =
            wire.data !== undefined
              ? wire.data
              : wire.payload !== undefined
                ? wire.payload
                : raw;
          // Live performance snapshots require their producer timestamp; using
          // receipt time would incorrectly promote malformed/stale data to fresh.
          if (endpoint === "performance" && ["web_vitals_update", "pages_update", "api_update", "errors_update", "system_update"].includes(wire.type ?? "") &&
              (wire.timestamp === undefined || !Number.isFinite(new Date(wire.timestamp).getTime()))) {
            this.logger.warn("[WebSocket] dropped performance snapshot without a valid producer timestamp");
            return;
          }
          // Coerce timestamp (string | number | undefined) → Date.
          let ts: Date;
          if (
            typeof wire.timestamp === "string" ||
            typeof wire.timestamp === "number"
          ) {
            ts = new Date(wire.timestamp);
            if (isNaN(ts.getTime())) {
              ts = new Date();
            }
          } else {
            ts = new Date();
          }

          const message: WebSocketMessage = {
            type: (wire.type as WebSocketMessage["type"]) || "market_update",
            data,
            timestamp: ts,
            source: endpoint,
          };

          const subject = this.messageSubjects.get(endpoint);
          if (subject) {
            subject.next(message);
          }
        } catch (error) {
          this.logger.error(
            `Error parsing WebSocket message from ${endpoint}:`,
            { error },
          );
        }
      };

      ws.onerror = (error) => {
        if (this.websockets.get(endpoint) !== ws) {
          return;
        }
        // The browser `WebSocket.onerror` Event is OPAQUE — it carries no code,
        // reason, or message (`{ isTrusted: true }` only); the spec deliberately
        // withholds detail, so it has no actionable content.
        // ADMIN-INTERFACE-73: `warn` was NOT sufficient — staging logs warn→Sentry
        // (captureMessage), so the noise still leaked. Log at `debug` (below
        // prod/staging thresholds → never reaches Sentry) AND it is additionally
        // dropped from Sentry by isExpectedHandledNoise (the /WebSocket error on/
        // match) as a systemic backstop. The ACTIONABLE failure signal is preserved
        // elsewhere — `onclose` logs with the real close code, and the reconnect
        // schedule logs at info.
        this.logger.debug(`WebSocket error on ${endpoint}:`, { event: error });
        this.healthyEndpoints.delete(endpoint);
        this.setEndpointState(endpoint, "error");
        this.retireTransport(endpoint, ws, "Transport error");
      };

      ws.onclose = () => {
        if (this.websockets.get(endpoint) !== ws) {
          return;
        }
        this.logger.info(`WebSocket disconnected from ${endpoint}`);
        this.handleTransportLoss(endpoint, url, config);
      };
    } catch (error) {
      this.logger.error(`Failed to create WebSocket for ${endpoint}:`, {
        error,
      });
      this.handleTransportLoss(endpoint, url, config);
    }
  }

  private buildWebSocketUrl(endpoint: string): string {
    // Get typed WebSocket URLs from config store
    const wsEndpoints = this.configStore.ws();

    // Map endpoint names to configured URLs
    const endpointMap: { [key: string]: string } = {
      "market-data": wsEndpoints.marketData,
      trading: wsEndpoints.trading,
      admin: wsEndpoints.admin,
      risk: wsEndpoints.risk,
      "order-book": wsEndpoints.orderBook,
      "symbol-mapping": wsEndpoints.symbolMapping,
      "system-health": wsEndpoints.systemHealth,
      alerts: wsEndpoints.alerts,
      settings: `${wsEndpoints.base}/settings-sync`,
      // data-quality currently uses HTTP polling (see DataQualityService).
      // WS transport will be added when the backend handler is implemented.
      "data-quality": `${wsEndpoints.base}/data-quality`,
      ws: wsEndpoints.base,
      websocket: wsEndpoints.base,
    };

    // Determine base URL
    let baseUrl: string;
    if (endpointMap[endpoint]) {
      baseUrl = endpointMap[endpoint];
    } else if (!endpoint) {
      baseUrl = wsEndpoints.base;
    } else {
      // For unknown endpoints, append to base WebSocket URL
      const baseWsUrl = wsEndpoints.base;
      baseUrl = baseWsUrl.endsWith("/")
        ? `${baseWsUrl}${endpoint}`
        : `${baseWsUrl}/${endpoint}`;
    }

    // SECURITY: Do NOT append JWT token to URL (exposes token in logs, history, referrer).
    // Upgrade authentication uses Sec-WebSocket-Protocol in createWebSocket;
    // the post-open envelope remains a compatibility refresh signal.
    return baseUrl;
  }

  private startHeartbeat(endpoint: string, interval: number): void {
    const heartbeatInterval = setInterval(
      () => this.sendHeartbeat(endpoint),
      interval,
    );

    this.heartbeatIntervals.set(endpoint, heartbeatInterval);
  }

  private clearHeartbeatDeadline(endpoint: string): void {
    const deadline = this.heartbeatDeadlines.get(endpoint);
    if (deadline !== undefined) {
      clearTimeout(deadline);
    }
    this.heartbeatDeadlines.delete(endpoint);
  }

  private clearConnectionTimers(endpoint: string): void {
    const heartbeat = this.heartbeatIntervals.get(endpoint);
    if (heartbeat !== undefined) {
      clearInterval(heartbeat);
    }
    this.heartbeatIntervals.delete(endpoint);
    this.clearHeartbeatDeadline(endpoint);
    const reconnect = this.reconnectTimers.get(endpoint);
    if (reconnect !== undefined) {
      clearTimeout(reconnect);
    }
    this.reconnectTimers.delete(endpoint);
  }

  private sendHeartbeat(endpoint: string): void {
    const ws = this.websockets.get(endpoint);
    if (
      !ws ||
      ws.readyState !== WebSocket.OPEN ||
      this.document.visibilityState === "hidden" ||
      this.heartbeatDeadlines.has(endpoint)
    ) {
      return;
    }
    const config = this.endpointConfigs.get(endpoint);
    try {
      if (endpoint === "performance" || endpoint === "settings") {
        // These typed protocols use an integer epoch timestamp.
        ws.send(JSON.stringify({ type: "ping", timestamp: Date.now() }));
      } else {
        this.send(endpoint, { type: "ping", payload: {} });
      }
    } catch {
      this.retireTransport(endpoint, ws, "Heartbeat send failed");
      return;
    }
    const deadline = setTimeout(() => {
      this.heartbeatDeadlines.delete(endpoint);
      if (
        this.websockets.get(endpoint) !== ws ||
        this.document.visibilityState === "hidden"
      ) {
        return;
      }
      this.retireTransport(endpoint, ws, "Heartbeat response timed out");
    }, config?.heartbeatTimeout ?? 30000);
    this.heartbeatDeadlines.set(endpoint, deadline);
  }

  private retireTransport(endpoint: string, ws: WebSocket, reason: string): void {
    // A synchronous status subscriber may have already released this endpoint.
    if (this.websockets.get(endpoint) !== ws || !this.subscriberCounts.has(endpoint)) return;
    ws.onclose = null;
    ws.onerror = null;
    ws.onmessage = null;
    ws.onopen = null;
    try { ws.close(4000, reason); }
    catch { this.logger.debug(`WebSocket close failed for ${endpoint}`); }
    const config = this.endpointConfigs.get(endpoint) ?? this.defaultConfig;
    this.handleTransportLoss(endpoint, config.url ?? this.buildWebSocketUrl(endpoint), config);
  }

  private handleTransportLoss(
    endpoint: string,
    url: string,
    config: Partial<WebSocketConfig>,
  ): void {
    if (!this.subscriberCounts.has(endpoint) || !this.messageSubjects.has(endpoint)) return;
    this.clearConnectionTimers(endpoint);
    this.healthyEndpoints.delete(endpoint);
    const attempts = this.reconnectAttempts.get(endpoint) ?? 0;
    const maximum = config.maxReconnectAttempts ?? 10;
    if (!config.reconnect || attempts >= maximum) {
      this.exhaustedEndpoints.add(endpoint);
      if (!config.reconnect) {
        this.logger.debug(`Automatic reconnect disabled for ${endpoint}`);
      } else if (!config.enablePollingFallback) {
        this.logger.warn(`Max reconnection attempts (${maximum}) reached for ${endpoint}, giving up`);
      }
      this.setEndpointState(endpoint, "disconnected");
      if (!this.subscriberCounts.has(endpoint) || !this.messageSubjects.has(endpoint)) return;
      // Continue a bounded-rate recovery probe while HTTP fallback supplies data.
      if (config.reconnect && config.enablePollingFallback) {
        this.reconnectTimers.set(
          endpoint,
          setTimeout(() => {
            this.reconnectTimers.delete(endpoint);
            if (this.messageSubjects.has(endpoint)) {
              this.createWebSocket(endpoint, url, config);
            }
          }, config.reconnectInterval ?? 5000),
        );
      }
      return;
    }
    this.reconnectAttempts.set(endpoint, attempts + 1);
    this.setEndpointState(endpoint, "disconnected");
    if (!this.subscriberCounts.has(endpoint) || !this.messageSubjects.has(endpoint)) return;
    const delay = Math.min(
      (config.reconnectInterval ?? 5000) * Math.pow(1.5, attempts),
      60000,
    );
    this.logger.info(`Scheduling reconnect to ${endpoint} (attempt ${attempts + 1}/${maximum}) in ${Math.round(delay / 1000)}s`);
    this.reconnectTimers.set(
      endpoint,
      setTimeout(() => {
        this.reconnectTimers.delete(endpoint);
        if (this.messageSubjects.has(endpoint)) {
          this.createWebSocket(endpoint, url, config);
        }
      }, delay),
    );
  }

  /**
   * Send authentication message via WebSocket after connection
   * SECURITY: JWT is sent as message payload, NOT in URL (prevents log/history/referrer leaks)
   */
  private sendAuthenticationMessage(endpoint: string): void {
    const token = this.authService.getToken();
    if (token) {
      const ws = this.websockets.get(endpoint);
      if (ws && ws.readyState === WebSocket.OPEN) {
        const authMessage = {
          id: crypto.randomUUID(),
          type: "authenticate",
          topic: "auth",
          payload: {
            token: token,
            auth_type: "jwt",
          },
          timestamp: new Date().toISOString(),
        };
        ws.send(JSON.stringify((endpoint === "performance" || endpoint === "settings") ? { type: "authenticate", token } : authMessage));
        this.authenticatedEndpoints.add(endpoint);
        this.pendingAuthEndpoints.delete(endpoint);
        this.logger.debug(`Sent authentication message to ${endpoint}`);
      }
    } else {
      // Token not available yet - mark endpoint as pending authentication
      // Will be authenticated when token becomes available (login or refresh)
      this.pendingAuthEndpoints.add(endpoint);
      this.logger.debug(
        `No token available for ${endpoint} authentication - queued for later`,
      );
    }
  }

  /**
   * Re-authenticate all connected WebSocket endpoints
   * Called when authentication state changes (login, token refresh)
   */
  private reauthenticateAllConnections(): void {
    const token = this.authService.getToken();
    if (!token) {
      return;
    }

    // First, authenticate any pending endpoints that were waiting for a token
    for (const endpoint of this.pendingAuthEndpoints) {
      this.sendAuthenticationMessage(endpoint);
    }

    // Then, re-authenticate already connected endpoints (in case of token refresh)
    for (const [endpoint, ws] of this.websockets.entries()) {
      if (
        ws.readyState === WebSocket.OPEN &&
        !this.pendingAuthEndpoints.has(endpoint)
      ) {
        // Send new authentication with fresh token
        const authMessage = {
          id: crypto.randomUUID(),
          type: "authenticate",
          topic: "auth",
          payload: {
            token: token,
            auth_type: "jwt",
          },
          timestamp: new Date().toISOString(),
        };
        ws.send(JSON.stringify((endpoint === "performance" || endpoint === "settings") ? { type: "authenticate", token } : authMessage));
        this.authenticatedEndpoints.add(endpoint);
        this.logger.debug(
          `Re-authenticated WebSocket connection for ${endpoint}`,
        );
      }
    }
  }

  /**
   * Open every endpoint whose connect() was deferred while the user was
   * unauthenticated. Called on the authenticated transition. Each deferred
   * endpoint already has a live message Subject registered, so existing
   * subscribers begin receiving messages once the socket opens.
   */
  private drainDeferredEndpoints(): void {
    if (this.deferredEndpoints.size === 0) {
      return;
    }
    // Snapshot then clear so a synchronous re-defer (e.g. an immediate
    // disconnect) does not mutate the map mid-iteration.
    const pending = new Map(this.deferredEndpoints);
    this.deferredEndpoints.clear();
    for (const [endpoint, config] of pending) {
      // Skip if a socket already exists (e.g. another caller opened it).
      if (this.websockets.has(endpoint)) {
        continue;
      }
      const url = config.url || this.buildWebSocketUrl(endpoint);
      this.logger.debug(
        `Opening deferred WebSocket ${endpoint} after authentication`,
      );
      this.createWebSocket(endpoint, url, config);
    }
  }

  private sendInitialSubscription(endpoint: string): void {
    // First, replay any pending subscriptions that were queued while disconnected
    const pending = this.pendingSubscriptions.get(endpoint);
    if (pending && pending.length > 0) {
      this.logger.info(
        `Replaying ${pending.length} queued subscriptions for ${endpoint}`,
      );
      pending.forEach((sub) => {
        this.send(endpoint, sub);
      });
      this.pendingSubscriptions.delete(endpoint);
    }

    // Send subscription messages based on endpoint type
    // Messages are wrapped by send() with id, type, topic, payload, timestamp
    switch (endpoint) {
      case "market-data":
        if (this.hasExplicitMarketIntent) {
          for (const intent of this.desiredMarketSubscriptions.values()) { this.writeMessage(endpoint, intent); }
        } else {
          for (const symbol of ['BTC/USD', 'ETH/USD', 'SOL/USD']) {
            const topic = `market:${symbol}`;
            this.writeMessage(endpoint, { type: 'subscribe', topic, payload: { topic } });
            this.defaultMarketTopics.add(topic);
          }
        }
        break;

      case "system-health":
        this.send(endpoint, {
          type: "subscribe",
          payload: {
            metrics: ["cpu", "memory", "latency", "throughput"],
          },
        });
        break;

      case "orders":
        this.send(endpoint, {
          type: "subscribe",
          payload: {
            events: ["new", "filled", "cancelled", "rejected"],
          },
        });
        break;

      case "positions":
        this.send(endpoint, {
          type: "subscribe",
          payload: {
            updates: ["pnl", "exposure", "margin"],
          },
        });
        break;

      case "alerts":
        // No severity filter: the /ws/alerts filter is an allow-list, and an
        // empty list means every severity, so live updates match the
        // unfiltered snapshot (a partial list once dropped `error`; F-FU-38).
        this.send(endpoint, {
          type: "subscribe",
          payload: {},
        });
        break;

      case "metrics/live":
        this.send(endpoint, {
          type: "subscribe",
          payload: {
            // The production publisher currently owns these four process-local
            // categories. Unsupported categories are surfaced by the HFT UI as
            // unavailable instead of being subscribed and silently awaiting
            // events that cannot arrive.
            metrics: ["snapshot", "latency", "throughput", "system"],
          },
        });
        break;
    }
  }
}
